/* eslint-disable no-inner-declarations */
import moment from 'moment';
import * as _ from 'lodash';

import { DataExport } from './data-export';

import {
  PreciseDealPricingStatus,
  DealVersion,
  CallFeature,
  CallType,
  Maturity,
  SyndicateRole,
  ESGCertification,
  Metrics
} from '../../types/deal.types';
import { issueTypeValueGetter } from '../../grid/utils/value-getters/issue-type';
import { DealsSeries } from '../../helpers/deals/series';
import { seriesValueGetter } from '../../grid/utils/value-getters/series-getter';
import { dealDateValueGetter } from '../../grid/utils/value-getters/deal-date';
import {
  uniqueOutlooks,
  getOutlookLabel,
  getEnhancementLabels,
  uniqueFullRatingModels,
  getRatingValue,
  formatFullRatingsForExport,
  someAgencyHasAllRatingsEnhanced,
  someAgencyHasUnderlyingRating,
  extractUniqueRatings,
  extractUniqueUnderlyingRatings
} from '../../helpers/issue/ratings';
import {
  getBasicDealPricingStatus,
  getBasicDealPricingStatusForDealManager
} from '../../helpers/issue/deal-status';
import { DealSeries } from '../../helpers/issue/series';
import { ticketingValueGetter } from '../../grid/utils/value-getters/ticketing';
import { structureFormatter } from '../../grid/utils/formatters/structure';
import { structureExportValueGetter } from '../../grid/utils/value-getters/structure';
import { IssueOrderPeriod } from '../../helpers/issue/order-period';
import { OptionsType, DealMaturities } from '../../helpers/issue/maturity';
import { IssueDate } from '../../helpers/issue/date';
import { taxStatusGetterForSeries } from '../../grid/utils/value-getters/tax-status';
import { onlyUnique } from '../filters/only-unique';
import { getPricingDate } from '../../helpers/issue/pricing-date';
import { getSaleDate } from '../../helpers/issue/sale-date';
import { stringComparer } from '../comparers/primitive';
import {
  getEmptyToDashesValueMapper,
  getInlineArray,
  getVariousValueMapper,
  takeFirstValue
} from '../formatters/value-mappers';
import { descriptionFormatter } from '../../grid/utils/formatters/issue-description';
import { getPricingPrimaryValue } from '../../grid/utils/value-getters/pricing';
import { MaturityAnalyticsColumnName } from '../../grid/types/column.types';
import { Permissions } from '../../types/user-permissions.types';
import { getRefinedEsgData } from '../../grid/utils/value-getters/esg';
import { UserRole, User } from '../../types/users.types';
import { DealExportViewModel } from '../../types/export.types';
import { bidSubmittedFormatter } from '../../grid/utils/formatters/bid-submitted';
import { dealStatusValueGetter } from '../../grid/utils/value-getters/deal-status';
import { bidStatusGetter } from '../../grid/utils/value-getters/bid-status-getter';
import { FilterResultFacade } from '../../helpers/filters/filter-result-facade';
import { DealFilterResult } from '../../helpers/filters/filter-types';
import { ValueGetterParamsDealVersion } from '../../grid/utils/value-getters/value-getters.interface';
import { ropValueGetter } from '../../grid/utils/value-getters/rop-date';
import { DealManagementStatus } from '../../types/deal.viewmodel.types';

export interface PricingAnalyticsYearFilterModel {
  year: number;
  future: boolean;
}

export namespace DealDataExport {
  import ExportFormatProvider = DataExport.ExportFormatProvider;
  import ExportDataValueProvider = DataExport.ExportDataValueProvider;
  import ExportColumnDefinition = DataExport.ExportColumnDefinition;

  export class DealExportColumn extends ExportColumnDefinition {
    public formatProvider?: ExportFormatProvider;
    public conditionToInclude?: (
      data: DealVersion | DealVersion[],
      permissions?: Permissions
    ) => boolean;
    public optional: boolean;
    constructor(
      public headerName: string,
      public valueProvider: ExportDealValueProvider,
      options?: {
        formatProvider?: ExportFormatProvider;
        conditionToInclude?: (
          data: DealVersion | DealVersion[],
          permissions?: Permissions
        ) => boolean;
        mappers?: ((value: any) => any)[];
      }
    ) {
      super(options as unknown);
    }
  }
  export interface ExportDealValueProvider extends ExportDataValueProvider {
    getValue(deal: DealExportViewModel, context?: any): any;
  }

  export class RatingExportDealValueProvider
    implements ExportDealValueProvider {
    constructor(public ratingName: string) {}

    getValue(deal: DealVersion): string[] | string {
      const value = getDealRatingsExportValue(deal, this.ratingName);

      return this.constructCellValue(deal, value);
    }

    private constructCellValue(
      deal: DealVersion,
      cell: RatingsFullCell
    ): string {
      const cellContentParts: string[] = [];

      const rating = takeFirstValue(getVariousValueMapper('Var')(cell.ratings));
      if (rating) {
        cellContentParts.push(getEmptyToDashesValueMapper()(rating));
      }

      if (
        someAgencyHasUnderlyingRating(deal.series) ||
        someAgencyHasAllRatingsEnhanced(deal.series)
      ) {
        const underlyingRating: string = getEmptyToDashesValueMapper()(
          takeFirstValue(getVariousValueMapper('Var')(cell.underlyingRatings))
        );
        cellContentParts.push(underlyingRating);
      }

      if (cell.outlookLabels.length === 1) {
        cellContentParts.push(cell.outlookLabels[0]);
      } else {
        cellContentParts.push('Var');
      }

      if (cellContentParts.every((part) => part === '-')) {
        return '-';
      }

      return cellContentParts.join(' | ');
    }
  }

  export class RatingsBySeriesExportValueProvider
    implements ExportDealValueProvider {
    constructor(public agencyName: string) {}

    getValue(deal: DealVersion): string[] {
      const uniqueRatingPairs = uniqueFullRatingModels(
        deal.series,
        this.agencyName
      );

      return formatFullRatingsForExport(uniqueRatingPairs);
    }
  }

  abstract class BasePricingAnalyticsValueProvider
    implements ExportDealValueProvider {
    constructor(private yearFilterModel: PricingAnalyticsYearFilterModel) {}
    abstract getValue(
      deal: DealVersion,
      context: any
    ): string | (number | string)[];

    protected getFilteredMaturities(deal: DealVersion): Maturity[] {
      return deal.maturities.filter((maturity) => {
        if (this.yearFilterModel.future) {
          return (
            moment(maturity.maturityDate).year() >= this.yearFilterModel.year &&
            DealMaturities.Structure.isShared(maturity)
          );
        }
        return (
          DealMaturities.Structure.isShared(maturity) &&
          moment(maturity.maturityDate).year() === this.yearFilterModel.year
        );
      });
    }
  }

  export class DefaultPricingAnalyticsValueProvider extends BasePricingAnalyticsValueProvider {
    constructor(
      private field: MaturityAnalyticsColumnName,
      yearFilterModel: PricingAnalyticsYearFilterModel
    ) {
      super(yearFilterModel);
    }

    getValue(deal: DealVersion): string[] {
      const filteredMaturities = this.getFilteredMaturities(deal);

      return filteredMaturities.map(
        (m) => m[this.field] && m[this.field].toFixed(3)
      );
    }
  }

  export class SpeadPricingAnalyticsValueProvider extends BasePricingAnalyticsValueProvider {
    constructor(yearFilterModel: PricingAnalyticsYearFilterModel) {
      super(yearFilterModel);
    }
    getValue(deal: DealVersion, context: any): (number | string)[] {
      const filteredMaturities = this.getFilteredMaturities(deal);

      return getPricingPrimaryValue(
        filteredMaturities,
        deal,
        MaturityAnalyticsColumnName.Spread
      );
    }
  }

  export class MaturityYearPricingAnalyticsValueProvider extends BasePricingAnalyticsValueProvider {
    getValue(deal: DealVersion): string {
      const filteredMaturities = this.getFilteredMaturities(deal);
      return structureFormatter(
        structureExportValueGetter({
          data: { ...deal, maturities: filteredMaturities }
        })
      );
    }
  }

  export class DealPropertyValueProvider implements ExportDealValueProvider {
    constructor(private field: keyof DealExportViewModel) {}

    getValue(deal: DealExportViewModel): any {
      return deal[this.field];
    }
  }

  export class StringValueGetterDealValueProvider
    implements ExportDealValueProvider {
    constructor(public valueGetter: (arg) => unknown) {}

    getValue(deal): any {
      let value = this.valueGetter({ data: deal });
      value =
        Array.isArray(value) &&
        value.length === 1 &&
        value[0].indexOf(OptionsType.Blank) !== -1
          ? '-'
          : value;
      return value;
    }
  }

  export const pricingStatusValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return getBasicDealPricingStatus(deal) || '-';
    }
  };

  // used for Deal Manager(Exporting buyside as per the grid data)
  export const dealPricingStatusValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return getBasicDealPricingStatusForDealManager(deal) || '-';
    }
  };

  export const dealStatusValueProvider: ExportDealValueProvider = {
    getValue(deal): PreciseDealPricingStatus | undefined {
      return dealStatusValueGetter({ data: deal })?.pricingStatus;
    }
  };

  export const erpValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      const erp = DealSeries.erpValueGetter({ data: deal });
      return erp.length > 1 ? 'Partial' : erp[0] ? 'Yes' : 'No';
    }
  };

  export const sourceOfRepaymentValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return deal.series.map((s) => s.sourceOfRepayment);
    }
  };

  export const bankQualifiedValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      const isBankQualifiedOnSeriesLvl = deal.series.reduce((acc, s) => {
        const bq = s.isBankQualified;
        if (bq !== null) {
          acc.push(bq);
        }
        return acc;
      }, []);
      if (isBankQualifiedOnSeriesLvl.length) {
        return isBankQualifiedOnSeriesLvl.map((bq) => (bq ? 'Yes' : 'No'));
      }
      return deal.isBankQualified ? 'Yes' : 'No';
    }
  };

  export const purposeValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return purposeColumn(deal);
    }
  };

  export const firstCallDateValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return getInlineArray(firstCallColumn(deal), (cf) => cf);
    }
  };

  export const firstCouponDateValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      const moments: moment.Moment[] = DealSeries.getFirstCouponDateForSeries(
        deal.series
      );
      return moments;
    }
  };

  export class RoleValueProvider implements ExportDealValueProvider {
    getValue(deal): any {
      const dealManagementStatus = deal.managementStatus;

      if (dealManagementStatus === DealManagementStatus.LeadManaged) {
        return 'Lead Manager';
      } else if (dealManagementStatus === DealManagementStatus.CoManaged) {
        return 'Co Managed/Selling Group';
      } else if (dealManagementStatus === DealManagementStatus.NoRole) {
        return 'No Role';
      }
    }
  }

  export const seniorManagerValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return (deal.seniorManager && deal.seniorManager.name) || '-';
    }
  };

  export const getSyndicateMemberValueProvider = (
    role: SyndicateRole
  ): ExportDealValueProvider => ({
    getValue(deal): any {
      return (
        deal.syndicate &&
        deal.syndicate.reduce(
          (acc, sm) => [
            ...acc,
            ...(sm.role === role ? [sm.departmentName] : [])
          ],
          []
        )
      );
    }
  });

  export const issueDescriptionValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return deal.description ? descriptionFormatter({ data: deal }) : '';
    }
  };

  export const bidSubmissionValueProvider: ExportDealValueProvider = {
    getValue(deal): string {
      return bidSubmittedFormatter({ data: deal });
    }
  };

  export const issuerValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return deal.issuer ? deal.issuer.name : '';
    }
  };

  export const dealDateValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      const dealDate: IssueDate.DealWithFormattedDate = dealDateValueGetter(
        deal
      );
      return dealDate.dealDate
        ? _.cloneDeep(dealDate.dealDate).format('l')
        : dealDate.dealDateFormatted;
    }
  };

  export const bidStageValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return bidStatusGetter({ data: deal });
    }
  };

  export const sizeValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return Math.round(deal.size / 1000);
    }
  };

  export const dealTypeValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return deal.dealType;
    }
  };

  export const issueTypeValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): any {
      return deal.series.map(issueTypeValueGetter);
    }
  };

  export const dealStructureValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      return structureFormatter(
        structureExportValueGetter({
          data: deal
        })
      );
    }
  };

  export const timingStructureValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      let value = IssueOrderPeriod.getStartTimeTz(deal);
      const pricingStatus: string = pricingStatusValueProvider.getValue(deal);
      value =
        pricingStatus === PreciseDealPricingStatus.Expected
          ? value || ''
          : null;
      return value;
    }
  };

  export const ticketingStructureValueProvider: ExportDealValueProvider = {
    getValue(deal): any {
      let value = ticketingValueGetter({ data: deal });
      const pricingStatus: string = pricingStatusValueProvider.getValue(deal);
      value =
        pricingStatus === PreciseDealPricingStatus.Sold ||
        pricingStatus === PreciseDealPricingStatus.Priced
          ? value || ''
          : null;
      return value;
    }
  };

  export const datedDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): moment.Moment[] {
      return DealSeries.getDatedDateForSeries(deal.series);
    }
  };

  export const deliveryDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): moment.Moment[] {
      return DealSeries.getDeliveryDateForSeries(deal.series);
    }
  };

  export const awardDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): moment.Moment[] {
      return DealSeries.getAwardDateForSeries(deal.series);
    }
  };

  export const pricingDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      return getPricingDate(deal);
    }
  };

  export const salesDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      return getSaleDate(deal);
    }
  };

  export const tradeDateValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): moment.Moment[] {
      return DealSeries.getTradeDateForSeries(deal.series);
    }
  };

  export function createFilteredParValueProvider(
    filterResult: FilterResultFacade
  ): { getValue(deal: DealVersion): number } {
    return {
      getValue(deal: DealVersion): number {
        let result;
        const dealFilterResult: DealFilterResult = filterResult.getDealFilterResult(
          deal.id
        );

        if (dealFilterResult) {
          result = dealFilterResult.filteredPar
            ? dealFilterResult.filteredPar
            : dealFilterResult.totalPar;

          result = Math.round(result);
        }

        return result;
      }
    };
  }

  export class AssignmentsByRoleValueProvider
    implements ExportDealValueProvider {
    private readonly selectedRoles: UserRole[];
    constructor(role: UserRole) {
      this.selectedRoles = [role];
      if (role === UserRole.other) {
        this.selectedRoles.push(null);
      }
    }

    getValue(deal: DealExportViewModel): string[] {
      return deal.assignedUsersData
        .filter((user: User) => this.selectedRoles.includes(user.role))
        .map((user) => `${user.firstName} ${user.lastName}`);
    }
  }

  export const conduitIssuerValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      return deal.issuer ? deal.issuer.name : getConduitIssuer(deal);
    }
  };

  function getConduitIssuer(deal: DealVersion) {
    const conduitIssuer = deal.series
      .map(
        ({ conduitIssuerDepartment }) =>
          conduitIssuerDepartment && conduitIssuerDepartment.name
      )
      .filter((data) => !!data)
      .join(', ');

    return conduitIssuer;
  }

  export const obligorValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      return deal.series
        .map(({ obligor }) => obligor && obligor.name)
        .filter((data, index, arr) => !!data && arr.indexOf(data) === index)
        .join(', ');
    }
  };

  export const taxTypeValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      return deal.series.map((series) =>
        taxStatusGetterForSeries(series, false)
      );
    }
  };

  export const interestTypeValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      return deal.series.map((series) => series.interestType);
    }
  };

  export const securityTypeValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      return deal.maturities.map((maturity) =>
        maturity.securityType === 'Option' ? 'Put' : maturity.securityType
      );
    }
  };

  export const enhancementValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      const enhancements = deal.series.map((s) => s.insurer).filter(onlyUnique);
      return getEnhancementLabels(enhancements, true);
    }
  };

  export const financialAdvisorsValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      return (
        deal.financialAdvisors &&
        deal.financialAdvisors.map((fa) => fa.name).sort(stringComparer)
      );
    }
  };

  export const bondCounselsValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      return (
        deal.bondCounsels &&
        deal.bondCounsels.map((bc) => bc.name).sort(stringComparer)
      );
    }
  };

  export const ropValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      const params: ValueGetterParamsDealVersion = { data: deal };
      return ropValueGetter(params);
    }
  };

  export const esgValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string[] {
      const esgCertificationData: ESGCertification[] = deal.series.map(
        (series) => series.esgCertification
      );
      const esg = getRefinedEsgData(esgCertificationData);
      const result = esg
        ? esg.esgCertVerifier.toString() === esg.esgCertType.toString()
          ? [
              `${esg.esgCertType}${
                esg.esgPreapprovedInd ? ', Pre-Qualified' : ''
              }`
            ]
          : [
              `${esg.esgCertType}, ${esg.esgCertVerifier}${
                esg.esgPreapprovedInd ? ', Pre-Qualified' : ''
              }`
            ]
        : ['-'];
      return result;
    }
  };

  export const parityPlusValueProvider: ExportDealValueProvider = {
    getValue(deal: DealVersion): string {
      return deal.parityPlus ? 'Yes' : 'No';
    }
  };

  export const getInternalTrackingValueProvider = (
    internalTrackingParams: keyof Metrics
  ): ExportDealValueProvider => ({
    getValue(deal: DealVersion): Metrics[keyof Metrics] {
      return deal.metrics && deal.metrics[internalTrackingParams];
    }
  });
}

function purposeColumn(deal: DealVersion): string[] {
  let subSectorData = seriesValueGetter('subSectors')({ data: deal }).map(
    (subSectorCode) => DealsSeries.Sector.getSubSectors()[subSectorCode]
  );
  const filteredSubSectorData = subSectorData.filter((data) => !data.ignorable);
  if (filteredSubSectorData.length) {
    subSectorData = filteredSubSectorData;
  }
  return subSectorData.map((data) => data.label);
}

export function firstCallColumn(deal: DealVersion): string[] {
  const firstCallDates = new Set(
    deal.series
      .map(({ callFeatures, id }) =>
        callFeatures.length
          ? callFeatures.map((callFeature) => prepareCallFeature(callFeature))
          : deal.maturities
              .filter((m) => m.seriesId === id && m)
              .map(({ callFeature }) => prepareCallFeature(callFeature))
              .reduce(
                (acc, curr) => (!acc.includes(curr) ? acc.concat(curr) : acc),
                []
              )
      )
      .reduce((acc, curr) => acc.concat(curr), [])
  );
  return Array.from(firstCallDates);
}

function prepareCallFeature(callFeature: CallFeature | null): string {
  if (!callFeature) {
    return ' ';
  }
  switch (callFeature.type) {
    case CallType.callable:
      return moment(callFeature.firstCallDate).format('MM/DD/YYYY');
    case CallType.notCallable:
      return '-';
    case CallType.makeWholeCall:
      return 'MWC';
    case CallType.callableAnytime:
      return 'Anytime';
    default:
      return ' ';
  }
}
export interface RatingsFullCell {
  ratings: string[];
  underlyingRatings: string[];
  outlookLabels: string[];
}

function getDealRatingsExportValue(
  data: DealVersion,
  agencyName: string
): RatingsFullCell {
  const ratings = extractUniqueRatings(data.series, agencyName);
  const underlyingRatings = extractUniqueUnderlyingRatings(
    data.series,
    agencyName
  );

  const resultRatings = ratings.map((r) => getRatingValue(r));

  const resultUnderlyingRatings = underlyingRatings.map((r) =>
    getRatingValue(r)
  );

  const outlookLabels = uniqueOutlooks(ratings).map(getOutlookLabel);

  return {
    ratings: resultRatings,
    underlyingRatings: resultUnderlyingRatings,
    outlookLabels
  };
}
